import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowLeft,
  LuBriefcaseBusiness,
  LuClipboardList,
  LuDownload,
  LuEllipsisVertical,
  LuFileText,
  LuInfo,
  LuLayers,
  LuLock,
  LuMailQuestion,
  LuPaperclip,
  LuSend,
  LuShield,
  LuTriangleAlert,
  LuUserRound,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  TICKET_STATUS_OPTIONS,
  fetchManagerTicketById,
  fetchSuperAdminTicketById,
  fetchSupportTicketById,
  postSupportTicketMessage,
  ticketExtractMessage,
  updateSupportTicketStatus,
} from "../../services/supportTicket.service";
import "../../assets/styles/support_disputes.scss";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isImageAttachment(mime, name = "") {
  if (String(mime || "").toLowerCase().startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(String(name || ""));
}

function senderLabel(type) {
  if (type === "support" || type === "manager") return "Support";
  if (type === "customer") return "Customer";
  if (type === "system") return "System";
  return "User";
}

function resolveTicketFetch(mode) {
  if (mode === "manager") return fetchManagerTicketById;
  if (mode === "super-admin") return fetchSuperAdminTicketById;
  return fetchSupportTicketById;
}

function resolvePermissions(mode) {
  if (mode === "support") {
    return {
      canUpdateStatus: true,
      canSendPublicMessages: true,
      canAddInternalNotes: true,
      statusRestrictionMessage: null,
    };
  }
  if (mode === "manager") {
    return {
      canUpdateStatus: false,
      canSendPublicMessages: false,
      canAddInternalNotes: false,
      statusRestrictionMessage:
        "Support Managers monitor tickets. Reassign before operational handling.",
    };
  }
  return {
    canUpdateStatus: false,
    canSendPublicMessages: false,
    canAddInternalNotes: false,
    statusRestrictionMessage: "Super Admin has read-only ticket oversight.",
  };
}

export default function SupportTicketDetailPage({
  mode = "support",
  backPath = "/dashboard/support/tickets",
  backLabel = "My Assigned Tickets",
}) {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [message, setMessage] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [showAllTimeline, setShowAllTimeline] = useState(false);

  const fetchDetail = resolveTicketFetch(mode);
  const permissions = resolvePermissions(mode);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDetail(ticketId);
      if (!data?.ticket) throw new Error("Ticket not found.");
      setDetail(data);
      setStatus(data.ticket.status);
      setResolutionNotes(data.ticket.resolutionNotes || "");
      if (!permissions.canSendPublicMessages) setIsInternal(true);
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to load ticket."));
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  }, [backPath, fetchDetail, navigate, permissions.canSendPublicMessages, ticketId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages]);

  useEffect(() => {
    if (mode === "support") return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchDetail(ticketId)
          .then((data) => {
            if (data?.ticket) setDetail(data);
          })
          .catch(() => {});
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [fetchDetail, mode, ticketId]);

  const onSaveStatus = async () => {
    if (!permissions.canUpdateStatus) {
      toast.error(permissions.statusRestrictionMessage || "You cannot update this ticket status.");
      return;
    }
    if (!status) {
      toast.error("Please select a status.");
      return;
    }
    setSavingStatus(true);
    try {
      const data = await updateSupportTicketStatus(ticketId, {
        status,
        resolutionNotes:
          status === "resolved" || status === "closed" ? resolutionNotes : undefined,
      });
      setDetail(data);
      toast.success("Status updated.");
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to update status."));
    } finally {
      setSavingStatus(false);
    }
  };

  const onSendMessage = async (event) => {
    event.preventDefault();
    if (!permissions.canSendPublicMessages && !permissions.canAddInternalNotes) return;
    if (!message.trim() && !attachment) {
      toast.error("Enter a message or attach a file.");
      return;
    }
    setSending(true);
    try {
      const sendAsInternal = permissions.canSendPublicMessages ? isInternal : true;
      const data = await postSupportTicketMessage(ticketId, {
        message: message.trim(),
        isInternal: sendAsInternal,
        attachment,
      });
      setDetail(data);
      setMessage("");
      setAttachment(null);
      setIsInternal(!permissions.canSendPublicMessages);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success(sendAsInternal ? "Internal note added." : "Message sent.");
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to send message."));
    } finally {
      setSending(false);
    }
  };

  const beginInternalNote = () => {
    if (!permissions.canAddInternalNotes && !permissions.canSendPublicMessages) {
      toast.info(permissions.statusRestrictionMessage || "Conversation is read-only.");
      return;
    }
    setIsInternal(true);
    messageInputRef.current?.focus();
  };

  const requestMoreInfo = () => {
    if (!permissions.canSendPublicMessages) {
      toast.info("Public replies are not available for this account.");
      return;
    }
    setIsInternal(false);
    setMessage("Please provide additional information and supporting documents for this ticket.");
    messageInputRef.current?.focus();
  };

  const escalateTicket = () => {
    if (!permissions.canUpdateStatus) {
      toast.info(permissions.statusRestrictionMessage || "Status updates are not available.");
      return;
    }
    setStatus("under_investigation");
    toast.info("Status set to Under Investigation. Click Update Status to confirm.");
  };

  if (loading || !detail?.ticket) {
    return (
      <div className="sd-page">
        <p className="sd-empty">Loading ticket details...</p>
      </div>
    );
  }

  const ticket = detail.ticket;
  const messages = Array.isArray(detail.messages) ? detail.messages : [];
  const activities = Array.isArray(detail.activities) ? detail.activities : [];
  const visibleTimeline = showAllTimeline ? activities : activities.slice(0, 3);
  const ticketAttachments = Array.isArray(detail.attachments) ? detail.attachments : [];
  const attachments = [
    ...ticketAttachments.map((file) => ({
      id: `att-${file.id}`,
      name: file.fileName || "Attachment",
      path: file.filePath,
    })),
    ...messages
      .filter((item) => item.attachmentPath)
      .map((item) => ({
        id: `message-${item.id}`,
        name: item.attachmentName || "Message attachment",
        path: item.attachmentPath,
      })),
  ];
  const customerPhone = [ticket.customerCountryCode, ticket.customerPhone]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <div className="sd-page sd-detail-page">
      <nav className="sd-breadcrumb" aria-label="Breadcrumb">
        <Link to={backPath}>{backLabel}</Link>
        <span aria-hidden>&gt;</span>
        <span aria-current="page">{ticket.ticketNumber}</span>
      </nav>

      <header className="sd-detail-head">
        <div className="sd-detail-title-row">
          <button
            type="button"
            className="sd-icon-btn sd-icon-btn--ghost"
            onClick={() => navigate(backPath)}
            aria-label="Back"
          >
            <LuArrowLeft />
          </button>
          <div>
            <h1>{ticket.ticketNumber}</h1>
            <p>{ticket.subject}</p>
          </div>
        </div>
        <div className="sd-detail-badges">
          <span className={`sd-pill sd-pill-status sd-pill-status--${ticket.status}`}>
            {formatStatus(ticket.status, ticket.statusLabel)}
          </span>
          <span className={`sd-pill sd-pill--${String(ticket.priority || "").toLowerCase()}`}>
            {ticket.priority || "—"}
          </span>
          <span className={ticket.slaOverdue ? "sd-sla sd-sla--overdue" : "sd-sla"}>
            SLA: {ticket.slaRemainingLabel || "—"}
          </span>
          <button type="button" className="sd-icon-btn sd-icon-btn--ghost" aria-label="More actions">
            <LuEllipsisVertical />
          </button>
        </div>
      </header>

      <div className="sd-detail-shell">
        <main className="sd-detail-main">
          <section className="sd-party-grid">
            <article className="sd-card sd-info-card">
              <h2>
                <LuClipboardList aria-hidden /> Ticket Summary
              </h2>
              <dl>
                <dt>Category</dt>
                <dd>{ticket.categoryLabel || "—"}</dd>
                <dt>Priority</dt>
                <dd>{ticket.priority || "—"}</dd>
                <dt>Submitted On</dt>
                <dd>{formatDateTime(ticket.createdAt)}</dd>
                <dt>Last Updated</dt>
                <dd>{formatDateTime(ticket.updatedAt)}</dd>
              </dl>
            </article>
            <article className="sd-card sd-info-card">
              <h2>
                <LuLayers aria-hidden /> Ticket Details
              </h2>
              <dl>
                <dt>Affected Module</dt>
                <dd>{ticket.affectedModule || "—"}</dd>
                <dt>Reference No.</dt>
                <dd>{ticket.referenceNumber || "—"}</dd>
                <dt>Expected Response</dt>
                <dd>{ticket.expectedResponseLabel || "—"}</dd>
                <dt>SLA Due</dt>
                <dd>{formatDateTime(ticket.slaDueAt)}</dd>
              </dl>
            </article>
          </section>

          <section className="sd-party-grid">
            <article className="sd-card sd-info-card">
              <h2>
                <LuBriefcaseBusiness aria-hidden /> Assignment Information
              </h2>
              <dl>
                <dt>Assigned To</dt>
                <dd>{ticket.assignedToName || "—"}</dd>
                <dt>Assignment Type</dt>
                <dd>
                  {ticket.assignmentType === "manual"
                    ? "Manual"
                    : ticket.assignmentType === "automatic"
                      ? "Automatic (Round Robin)"
                      : "Unassigned"}
                </dd>
                <dt>Assigned At</dt>
                <dd>{formatDateTime(ticket.assignedAt)}</dd>
                <dt>Manager</dt>
                <dd>{ticket.managerName || "—"}</dd>
                <dt>Assignee Email</dt>
                <dd>{ticket.assignedToEmail || "—"}</dd>
              </dl>
            </article>
            <article className="sd-card sd-info-card">
              <h2>
                <LuUserRound aria-hidden /> Customer Information
              </h2>
              <dl>
                <dt>Name</dt>
                <dd>{ticket.customerName || "—"}</dd>
                <dt>Email</dt>
                <dd>{ticket.customerEmail || "—"}</dd>
                <dt>Phone</dt>
                <dd>{customerPhone || "—"}</dd>
              </dl>
            </article>
          </section>

          <section className="sd-card sd-info-card sd-description-card">
            <h2>
              <LuFileText aria-hidden /> Full Description
            </h2>
            <div className="sd-description-body">
              {ticket.description?.trim()
                ? ticket.description.trim()
                : "No description provided."}
            </div>
          </section>

          <section className="sd-card sd-conversation">
            <header className="sd-conversation__head">
              <div>
                Conversation
                <small>
                  {permissions.canSendPublicMessages
                    ? "Communicate with the customer. All messages are recorded."
                    : "View the complete conversation history."}
                </small>
              </div>
              {permissions.canSendPublicMessages ? (
                <label className="sd-internal-toggle">
                  <span>Internal Note</span>
                  <input
                    type="checkbox"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                  />
                  <i aria-hidden />
                </label>
              ) : (
                <span className="sd-internal-only">
                  <LuLock aria-hidden /> Read-only
                </span>
              )}
            </header>
            <div className={`sd-chat${messages.length === 0 ? " sd-chat--empty" : ""}`}>
              <div className="sd-chat__thread" aria-live="polite">
                {messages.length === 0 ? (
                  <p className="sd-empty">No messages yet.</p>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`sd-chat__bubble sd-chat__bubble--${
                        msg.senderType === "customer" ? "buyer" : msg.senderType || "system"
                      }${msg.isInternal ? " is-internal" : ""}`}
                    >
                      <header>
                        <strong>
                          {msg.senderName || "User"}{" "}
                          <span>({senderLabel(msg.senderType)})</span>
                        </strong>
                        {msg.isInternal ? (
                          <em>
                            <LuLock aria-hidden /> Internal
                          </em>
                        ) : null}
                        <time>{formatDateTime(msg.createdAt)}</time>
                      </header>
                      <p>{msg.messageBody}</p>
                      {msg.attachmentPath ? (
                        isImageAttachment(msg.attachmentMime, msg.attachmentName) ? (
                          <a
                            className="sd-chat__image"
                            href={resolvePublicFileUrl(msg.attachmentPath)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              src={resolvePublicFileUrl(msg.attachmentPath)}
                              alt={msg.attachmentName || "Conversation attachment"}
                            />
                          </a>
                        ) : (
                          <a
                            className="sd-chat__file"
                            href={resolvePublicFileUrl(msg.attachmentPath)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <LuPaperclip aria-hidden /> {msg.attachmentName || "Attachment"}
                          </a>
                        )
                      ) : null}
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {permissions.canAddInternalNotes || permissions.canSendPublicMessages ? (
                <form className="sd-chat__composer" onSubmit={onSendMessage}>
                  {isInternal ? (
                    <div className="sd-internal-banner">
                      <LuShield aria-hidden /> Internal note — hidden from customer
                    </div>
                  ) : null}
                  <textarea
                    ref={messageInputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      isInternal
                        ? "Write an internal note for support / admin..."
                        : "Write a message to the customer..."
                    }
                    rows={3}
                  />
                  <div className="sd-chat__composer-actions">
                    {permissions.canSendPublicMessages ? (
                      <label className="sd-attach-action">
                        <LuPaperclip aria-hidden /> Attach File
                        <input
                          ref={fileInputRef}
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                          onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                        />
                      </label>
                    ) : null}
                    {attachment ? <small>{attachment.name}</small> : null}
                    <button type="submit" className="sd-btn sd-btn--primary" disabled={sending}>
                      <LuSend aria-hidden /> {sending ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="sd-permission-notice" role="note">
                  <LuLock aria-hidden />
                  <p>Conversation is read-only for this account.</p>
                </div>
              )}
            </div>
          </section>

          <section className="sd-card sd-activity-card">
            <header>
              <h2>Activity Timeline</h2>
              {activities.length > 3 ? (
                <button
                  type="button"
                  onClick={() => setShowAllTimeline((current) => !current)}
                  aria-expanded={showAllTimeline}
                >
                  {showAllTimeline ? "Show Less" : `View All (${activities.length})`}
                </button>
              ) : null}
            </header>
            <ol className="sd-timeline">
              {activities.length === 0 ? (
                <li className="sd-empty">No activity recorded yet.</li>
              ) : (
                visibleTimeline.map((item) => (
                  <li key={item.id}>
                    <div className="sd-timeline__dot" aria-hidden />
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.remarks || "—"}</p>
                      <small>
                        {item.performedByName || item.performedByType} ·{" "}
                        {formatDateTime(item.createdAt)}
                      </small>
                    </div>
                  </li>
                ))
              )}
            </ol>
          </section>
        </main>

        <aside className="sd-detail-side">
          <section className="sd-card sd-status-card">
            <h2>
              <LuUserRound aria-hidden /> Status Management
            </h2>
            {permissions.canUpdateStatus ? (
              <>
                <label className="sd-field">
                  <span>Current Status</span>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {TICKET_STATUS_OPTIONS.filter((opt) => opt.value && opt.value !== "new").map(
                      (opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ),
                    )}
                  </select>
                </label>
                {(status === "resolved" || status === "closed") && (
                  <label className="sd-field">
                    <span>Resolution / Closure Notes</span>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      rows={4}
                      placeholder="Summarize the resolution or closure reason"
                    />
                  </label>
                )}
                <button
                  type="button"
                  className="sd-btn sd-btn--primary sd-btn--block"
                  disabled={savingStatus}
                  onClick={onSaveStatus}
                >
                  {savingStatus ? "Saving..." : "Update Status"}
                </button>
              </>
            ) : (
              <div className="sd-permission-notice" role="note">
                <LuLock aria-hidden />
                <p>{permissions.statusRestrictionMessage}</p>
              </div>
            )}
            <ol className="sd-status-flow">
              {TICKET_STATUS_OPTIONS.filter(
                (item) => item.value && !["new", "reopened"].includes(item.value),
              ).map((item) => (
                <li key={item.value} className={item.value === ticket.status ? "is-current" : ""}>
                  <i aria-hidden />
                  <span>{item.label}</span>
                  {item.value === ticket.status ? <small>Current status</small> : null}
                </li>
              ))}
            </ol>
          </section>

          <section className="sd-card sd-attachments-card">
            <h2>
              <LuPaperclip aria-hidden /> Attachments ({attachments.length})
            </h2>
            {attachments.length ? (
              <div className="sd-attachment-list">
                {attachments.map((item) => (
                  <a
                    key={item.id}
                    href={resolvePublicFileUrl(item.path)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LuFileText aria-hidden />
                    <span>{item.name}</span>
                    <LuDownload aria-hidden />
                  </a>
                ))}
              </div>
            ) : (
              <p className="sd-card-empty">No attachments added.</p>
            )}
          </section>

          <section className="sd-card sd-quick-actions">
            <h2>Quick Actions</h2>
            <button type="button" className="is-note" onClick={beginInternalNote}>
              <LuLock aria-hidden /> Add Internal Note
            </button>
            {permissions.canSendPublicMessages ? (
              <button type="button" className="is-info" onClick={requestMoreInfo}>
                <LuMailQuestion aria-hidden /> Request More Info
              </button>
            ) : null}
            {permissions.canUpdateStatus ? (
              <button type="button" className="is-danger" onClick={escalateTicket}>
                <LuTriangleAlert aria-hidden /> Escalate Ticket
              </button>
            ) : null}
          </section>

          <section className="sd-card sd-help-card">
            <LuInfo aria-hidden />
            <div>
              <h2>Need Help?</h2>
              <p>Check our help center or contact your team lead for assistance.</p>
              <Link to="/help-center">Visit Help Center</Link>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
